/**
 * Structured Logger for Frontend
 * Provides centralized logging with consistent formatting
 * Also pushes logs to DebugContext for on-screen viewing
 */

import { pushDebugLog } from '../contexts/DebugContext';

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  [key: string]: any;
}

class Logger {
  private logLevel: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    // In frontend, check if we're in development mode
    this.isDevelopment = import.meta.env.DEV;
    this.logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  private log(level: LogLevel, message: string, meta?: object): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...meta,
    };

    // Push to debug overlay (always, so logs are available when debug mode is enabled)
    try {
      pushDebugLog(level, message, meta);
    } catch {
      // Ignore errors from debug context (may not be initialized yet)
    }

    // Format output based on environment
    if (this.isDevelopment) {
      // Human-readable format for development
      const emoji = this.getEmoji(level);
      const prefix = `${emoji} [${new Date().toLocaleTimeString()}] ${level.toUpperCase()}:`;

      // Use appropriate console method
      switch (level) {
        case LogLevel.ERROR:
          console.error(prefix, message, meta || '');
          break;
        case LogLevel.WARN:
          console.warn(prefix, message, meta || '');
          break;
        case LogLevel.DEBUG:
          console.debug(prefix, message, meta || '');
          break;
        default:
          console.log(prefix, message, meta || '');
      }
    } else {
      // JSON format for production (better for log aggregation)
      console.log(JSON.stringify(logEntry));
    }
  }

  private getEmoji(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return '🔍';
      case LogLevel.INFO:
        return 'ℹ️';
      case LogLevel.WARN:
        return '⚠️';
      case LogLevel.ERROR:
        return '❌';
    }
  }

  debug(message: string, meta?: object): void {
    this.log(LogLevel.DEBUG, message, meta);
  }

  info(message: string, meta?: object): void {
    this.log(LogLevel.INFO, message, meta);
  }

  warn(message: string, meta?: object): void {
    this.log(LogLevel.WARN, message, meta);
  }

  error(message: string, error?: Error | unknown, meta?: object): void {
    const errorMeta =
      error instanceof Error
        ? {
            error: error.message,
            stack: error.stack,
            name: error.name,
            ...meta,
          }
        : { error: String(error), ...meta };

    this.log(LogLevel.ERROR, message, errorMeta);
  }

  // Convenience methods for common frontend patterns
  api(method: string, path: string, statusCode?: number, duration?: number): void {
    this.info(`${method} ${path}`, {
      method,
      path,
      statusCode,
      duration,
      type: 'api',
    });
  }

  cache(operation: string, key: string, hit?: boolean, meta?: object): void {
    this.debug(`Cache ${operation}: ${key}`, {
      operation,
      key,
      hit,
      type: 'cache',
      ...meta,
    });
  }

  weather(operation: string, meta?: object): void {
    this.info(`Weather: ${operation}`, {
      type: 'weather',
      ...meta,
    });
  }

  profile(operation: string, meta?: object): void {
    this.info(`Profile: ${operation}`, {
      type: 'profile',
      ...meta,
    });
  }
}

// Export singleton instance
export const logger = new Logger();
